<template>
    <div class="data-manual-mode">
        <div class="mode-header">
            <span>Источник данных: Ручной ввод</span>
            <PrimeButton
                label="Изменить"
                class="p-button-sm p-button-outlined"
                @click="$emit('change-source')"
            />
        </div>

        <!-- Empty state: no fields defined yet -->
        <div v-if="!hasFields" class="empty-state">
            <ManualFieldsSetup
                v-if="showFieldsSetup"
                :project-id="project.id"
                @created="onFieldsCreated"
                @needs-confirmation="onKeysNeedConfirmation"
                @cancel="showFieldsSetup = false"
            />

            <template v-else>
                <div class="setup-section">
                    <h3>Создайте структуру данных</h3>

                    <div class="two-options">
                        <div class="option-card" @click="showFieldsSetup = true">
                            <div class="option-icon">
                                <i class="pi pi-pencil"></i>
                            </div>
                            <div class="option-title">Задайте поля вручную</div>
                            <div class="option-description">
                                Укажите названия полей, которые будут подставляться
                                в шаблон грамоты.
                            </div>
                        </div>

                        <div class="option-divider">или</div>

                        <div class="option-card csv-option">
                            <CsvUploadZone
                                :project-id="project.id"
                                :has-data="false"
                                @uploaded="onCsvUploaded"
                                @needs-confirmation="onNeedsConfirmation"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <!-- Data table view -->
        <div v-else class="data-view">
            <DataTableView
                ref="dataTable"
                :columns="columns"
                :project-id="project.id"
                :editable="true"
                @row-added="onRowAdded"
                @row-updated="onRowUpdated"
                @row-deleted="onRowDeleted"
                @download-one="onDownloadOne"
            />

            <DataActions
                :project-id="project.id"
                :filename-key="filenameKey"
                :filename-options="filenameOptions"
                :row-count="initialData.rowCount || 0"
                :has-fields="hasFields"
                @update:filename-key="filenameKey = $event"
                @download-template="onDownloadTemplate"
                @download-all="onDownloadAll"
                @upload-csv="onUploadCsv"
                @needs-confirmation="onNeedsConfirmation"
            />
        </div>
    </div>
</template>

<script>
import ManualFieldsSetup from './ManualFieldsSetup.vue';
import CsvUploadZone from './CsvUploadZone.vue';
import DataTableView from './DataTableView.vue';
import DataActions from './DataActions.vue';
import api from "@/libs/api";

export default {
    name: "DataManualMode",
    components: {
        ManualFieldsSetup,
        CsvUploadZone,
        DataTableView,
        DataActions
    },
    props: {
        project: {
            type: Object,
            required: true
        },
        initialData: {
            type: Object,
            default: () => ({ keys: [], values: [] })
        }
    },
    emits: ['change-source', 'data-changed', 'needs-confirmation'],
    data() {
        return {
            columns: this.initialData.keys || [],
            filenameKey: 'row',
            showFieldsSetup: false
        };
    },
    computed: {
        hasFields() {
            return this.columns && this.columns.length > 0;
        },
        filenameOptions() {
            const opts = [{ label: 'По номеру строки', value: 'row' }];
            if (this.columns) {
                this.columns.forEach(col => {
                    opts.push({ label: col.name, value: col.name });
                });
            }
            return opts;
        }
    },
    watch: {
        initialData: {
            handler(data) {
                this.columns = data.keys || [];
            },
            immediate: true,
            deep: true
        }
    },
    methods: {
        onFieldsCreated(keys) {
            this.columns = keys;
            this.showFieldsSetup = false;
            this.$emit('data-changed');
        },
        onCsvUploaded() {
            this.$emit('data-changed');
        },
        onRowAdded() {
            // Table manages its own data via server
        },
        onRowUpdated() {
            // Table manages its own data via server
        },
        onRowDeleted() {
            // Table manages its own data via server
        },
        onDownloadOne(row) {
            api.downloadFileForNumber(this.project.id, row.row);
        },
        onDownloadTemplate() {
            api.downloadDataTemplate(this.project.id);
        },
        onDownloadAll() {
            api.downloadFiles(this.project.id, this.filenameKey);
        },
        onUploadCsv() {
            this.$emit('data-changed');
        },
        onNeedsConfirmation(data) {
            this.$emit('needs-confirmation', data);
        },
        onKeysNeedConfirmation(data) {
            this.$emit('needs-confirmation', {
                ...data,
                action: 'change_keys'
            });
        }
    }
}
</script>

<style scoped>
.data-manual-mode {
    padding: 16px;
}

.mode-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    font-weight: 600;
    font-size: 1.1rem;
}

.empty-state {
    padding: 20px;
}

.setup-section h3 {
    text-align: center;
    margin-bottom: 24px;
    color: #333;
}

.two-options {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 24px;
}

.option-card {
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 24px;
    width: 300px;
    cursor: pointer;
    transition: all 0.2s;
    background: #fff;
}

.option-card:hover {
    border-color: #3b82f6;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.csv-option {
    cursor: default;
}

.option-icon {
    font-size: 2rem;
    color: #3b82f6;
    margin-bottom: 12px;
}

.option-title {
    font-weight: 600;
    margin-bottom: 8px;
}

.option-description {
    color: #6c757d;
    font-size: 0.9rem;
    line-height: 1.4;
    margin-bottom: 16px;
}

.option-divider {
    color: #adb5bd;
    font-size: 0.9rem;
    text-transform: uppercase;
}

.data-view {
    margin-top: 16px;
}
</style>
